import { useState } from "react";
import { studioAPI, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { VIRAL } from "@/constants/testIds";
import { toast } from "sonner";
import { TrendingUp, ArrowRight, Flame } from "lucide-react";
import AskDirector from "@/components/AskDirector";

export default function ViralLab() {
    const { lang } = useApp();
    const [url, setUrl] = useState("");
    const [desc, setDesc] = useState("");
    const [busy, setBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const parsed = tryParseJSON(raw);

    const run = async () => {
        if (!desc) { toast.error(lang === "fr" ? "Décris ou colle la transcription" : "Describe or paste transcript"); return; }
        setBusy(true); setRaw("");
        try {
            const { result } = await studioAPI.viral({ url, description: desc, language: lang });
            setRaw(result);
        } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
        finally { setBusy(false); }
    };

    const a = parsed?.analysis || {};
    const score = Number(a.virality_score_0_100) || 0;

    return (
        <div data-testid={VIRAL.container} className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-4">
                <div>
                    <div className="overline">05 · Viral Lab</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Analyse un viral" : "Analyze a viral"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Colle une URL + description → obtiens 10 concepts originaux inspirés du même mécanisme (pas de copie)." : "Paste URL + description → get 10 original concepts inspired by the same mechanic (no copy)."}</p>
                    <div className="mt-3">
                        <AskDirector
                            testid="viral-ask-director"
                            prompt={lang === "fr" ? "Analyse cette idée avec moi et dis-moi ce qui peut la rendre plus virale." : lang === "es" ? "Analicemos esta idea juntos y dime qué puede hacerla más viral." : "Analyze this idea with me and tell me what could make it more viral."}
                            label={lang === "fr" ? "Analyser cette idée avec le Directeur" : lang === "es" ? "Analizar esta idea con el Director" : "Analyze this idea with the Director"}
                        />
                    </div>
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">URL</label>
                    <input data-testid={VIRAL.url} value={url} onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://youtube.com/shorts/…"
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]" />
                </div>
                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Transcription / notes" : "Transcript / notes"}</label>
                    <textarea data-testid={VIRAL.description} value={desc} onChange={(e) => setDesc(e.target.value)} rows={7}
                        placeholder={lang === "fr" ? "Un chat regarde la caméra, une voix off dit… 20M vues" : "A cat looks at the camera, voice-over says… 20M views"}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00] resize-none" />
                </div>
                <button data-testid={VIRAL.analyzeBtn} onClick={run} disabled={busy}
                    className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60">
                    {busy ? "···" : <>{lang === "fr" ? "Analyser + générer 10 concepts" : "Analyze + generate 10 concepts"} <ArrowRight className="w-4 h-4" /></>}
                </button>
            </div>

            <div data-testid={VIRAL.result} className="lg:col-span-3 rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[500px]">
                {!raw && !busy && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <TrendingUp className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Analyse + 10 concepts inspirés." : "Analysis + 10 inspired concepts."}</p>
                    </div>
                )}
                {busy && <div className="h-full flex items-center justify-center text-slate-400 font-mono-sf text-xs">DECODING MECHANIC ···</div>}
                {parsed && (
                    <div className="space-y-5">
                        {a && Object.keys(a).length > 0 && (
                            <div className="bg-gradient-to-br from-[#161D2A] to-[#0B111B] border border-[#FF4D00]/30 rounded p-4">
                                <div className="flex items-center justify-between">
                                    <div className="overline">Virality decoded</div>
                                    <div className="flex items-center gap-2">
                                        <Flame className="w-4 h-4 text-[#FF4D00]" />
                                        <span className="font-display text-3xl font-black">{score}</span>
                                        <span className="text-xs text-slate-500">/100</span>
                                    </div>
                                </div>
                                <div className="mt-3 h-1.5 bg-[#0F141C] rounded-full overflow-hidden">
                                    <div className="h-full flame-gradient" style={{ width: `${score}%` }} />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-2 mt-4">
                                    {["hook", "structure", "rhythm", "emotional_trigger", "subject", "twist", "cta", "duration_seconds", "series_potential"].map((k) => a[k] && (
                                        <div key={k} className="text-xs">
                                            <div className="overline">{k.replace("_", " ")}</div>
                                            <div className="text-slate-200 mt-1">{a[k]}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {parsed.concepts?.length > 0 && (
                            <div>
                                <div className="overline">10 concepts originaux</div>
                                <div className="mt-2 space-y-2">
                                    {parsed.concepts.map((c, i) => (
                                        <div key={i} className="bg-[#161D2A] border border-[#253046] rounded p-3 hover:border-[#FF4D00]/50 kinetic">
                                            <div className="flex gap-3">
                                                <div className="font-display text-2xl font-black text-[#FF4D00]">{String(i + 1).padStart(2, "0")}</div>
                                                <div className="flex-1">
                                                    <div className="font-display text-base font-bold uppercase">{c.title}</div>
                                                    <div className="text-xs text-[#FFB800] italic mt-1">« {c.hook} »</div>
                                                    <div className="text-xs text-slate-300 mt-1">{c.one_line_pitch}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {raw && !parsed && <pre className="text-xs text-slate-300 whitespace-pre-wrap">{raw}</pre>}
            </div>
        </div>
    );
}
